/**
 * App 路由壳层
 *
 * 路由约定（src/app/routes/）：
 *   - 每个 tsx 文件是「约定式路由」的薄壳，1-3 行 re-export 到 features 对应 ui；
 *   - __layout.tsx 是 Next.js 风格的根 layout，把 Outlet 放进 MobileShell；
 *   - routeTree.tsx 用 import.meta.glob 扫所有 shell，产出 react-router 路由树。
 *
 * App 层只负责：
 *   - 顶层 providers（QueryClient / AppStore / Sync / Toast）；
 *   - 把 /login 放在 layout 之外、未登录态全屏；
 *   - 把 layout 内的所有路由交给 routeTree。
 */

import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';

import { AppStoreProvider } from '../shared/lib/state/store';
import { useAppDispatch } from '../shared/lib/state/store';
import { SyncBootstrap } from '../shared/ui/SyncBootstrap';
import { ToastHost } from '../shared/ui/ToastHost';
import { ThemeProvider } from '../shared/theme/theme';
import { IconStyleProvider } from '../shared/ui/iconStyle';

import { routeTree, loginRoute } from './routes/routeTree';
import { SESSION_EXPIRED_EVENT } from '../shared/api/tokens';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5, retry: 1 } },
});

const PrepaintShellDismissal: React.FC = () => {
  React.useEffect(() => {
    const dismiss = (): boolean => {
      if (!document.querySelector('.fp-mobile-app, .fp-auth-app')) return false;
      if (document.querySelector('[data-route-fallback]')) return false;
      document.getElementById('prepaint-shell')?.remove();
      return true;
    };
    if (dismiss()) return undefined;
    const observer = new MutationObserver(() => {
      if (dismiss()) observer.disconnect();
    });
    observer.observe(document.getElementById('root') ?? document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return null;
};

/** 会话明确失效时清掉所有用户态查询，避免旧账号数据继续留在页面里。 */
const SessionExpiryBoundary: React.FC = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const handleSessionExpired = () => {
      queryClient.clear();
      dispatch({ type: 'reset' });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [dispatch, queryClient]);
  return null;
};

const router = createBrowserRouter([
  loginRoute,
  ...routeTree,
  { path: '*', element: <Navigate to="/home" replace /> },
]);

const Providers: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AppStoreProvider>
      <SessionExpiryBoundary />
      <ThemeProvider>
        <IconStyleProvider>
          <PrepaintShellDismissal />
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </IconStyleProvider>
      </ThemeProvider>
      <SyncBootstrap />
      <ToastHost />
    </AppStoreProvider>
  </QueryClientProvider>
);

export default function App() {
  return <Providers />;
}
